import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { AssessmentService } from '../services/assessmentService';

const { FiDatabase, FiCheck, FiX, FiRefreshCw, FiPlay } = FiIcons;

const DatabaseTest = () => {
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setLoading(true);
    const result = await AssessmentService.checkDatabaseConnection();
    setConnectionStatus(result.success ? 'connected' : 'error');
    setTestResults(prev => ({ ...prev, connection: result }));
    setLoading(false);
  };

  const runTest = async (testName, testFunction) => {
    setLoading(true);
    setTestResults(prev => ({ ...prev, [testName]: { loading: true } }));
    
    try {
      const result = await testFunction();
      setTestResults(prev => ({ 
        ...prev, 
        [testName]: { 
          success: result.success, 
          data: result.data, 
          error: result.error,
          loading: false
        } 
      }));
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        [testName]: { 
          success: false, 
          error: error.message,
          loading: false
        } 
      }));
    }
    setLoading(false);
  };

  const tests = [
    {
      name: 'connection',
      title: 'Database Connection',
      description: 'Test basic connection to Supabase',
      action: () => runTest('connection', AssessmentService.checkDatabaseConnection)
    },
    {
      name: 'testInsert',
      title: 'Test Data Insertion',
      description: 'Insert a test assessment record',
      action: () => runTest('testInsert', AssessmentService.testDataInsertion)
    },
    {
      name: 'analytics',
      title: 'Analytics View',
      description: 'Test analytics view query',
      action: () => runTest('analytics', AssessmentService.getAssessmentAnalytics)
    },
    {
      name: 'recent',
      title: 'Recent Assessments',
      description: 'Fetch recent assessment records',
      action: () => runTest('recent', () => AssessmentService.getRecentAssessments(5))
    }
  ];

  const getStatusIcon = (testName) => {
    const result = testResults[testName];
    if (result?.loading) return FiRefreshCw;
    if (result?.success) return FiCheck;
    if (result?.success === false) return FiX;
    return FiPlay;
  };

  const getStatusColor = (testName) => {
    const result = testResults[testName];
    if (result?.loading) return 'text-blue-500';
    if (result?.success) return 'text-green-500';
    if (result?.success === false) return 'text-red-500';
    return 'text-gray-400';
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-text-primary mb-4">
          Supabase Database Test
        </h1>
        <p className="text-text-secondary">
          Test database connectivity and functionality
        </p>
      </div>

      {/* Connection Status */}
      <div className="bg-bg-primary rounded-xl p-6 ernest-shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">Connection Status</h2>
          <div className={`flex items-center px-3 py-1 rounded-full text-sm ${
            connectionStatus === 'connected' 
              ? 'bg-green-100 text-green-700' 
              : connectionStatus === 'error'
              ? 'bg-red-100 text-red-700'
              : 'bg-blue-100 text-blue-700'
          }`}>
            <SafeIcon 
              icon={connectionStatus === 'connected' ? FiCheck : connectionStatus === 'error' ? FiX : FiRefreshCw} 
              className={`mr-2 ${connectionStatus === 'checking' ? 'animate-spin' : ''}`} 
            />
            {connectionStatus === 'connected' ? 'Connected' : 
             connectionStatus === 'error' ? 'Connection Error' : 'Checking...'}
          </div>
        </div>
        
        {testResults.connection && (
          <div className="bg-bg-light rounded-lg p-4">
            <pre className="text-sm text-text-secondary overflow-auto">
              {JSON.stringify(testResults.connection, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Test Suite */}
      <div className="bg-bg-primary rounded-xl p-6 ernest-shadow">
        <h2 className="text-xl font-semibold text-text-primary mb-6">Database Tests</h2>
        
        <div className="space-y-4">
          {tests.map((test) => (
            <motion.div
              key={test.name}
              className="border border-gray-1 rounded-lg p-4 hover:border-sage-300 transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-text-primary">{test.title}</h3>
                  <p className="text-sm text-text-secondary">{test.description}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon 
                    icon={getStatusIcon(test.name)} 
                    className={`text-lg ${getStatusColor(test.name)} ${
                      testResults[test.name]?.loading ? 'animate-spin' : ''
                    }`} 
                  />
                  <button
                    onClick={test.action}
                    disabled={loading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    Run Test
                  </button>
                </div>
              </div>
              
              {testResults[test.name] && !testResults[test.name].loading && (
                <div className="mt-3 bg-bg-light rounded-lg p-3">
                  <div className="text-sm">
                    <div className={`font-medium mb-2 ${
                      testResults[test.name].success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {testResults[test.name].success ? '✅ Success' : '❌ Failed'}
                    </div>
                    <pre className="text-xs text-text-secondary overflow-auto max-h-40">
                      {JSON.stringify(testResults[test.name], null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={() => {
              tests.forEach(test => test.action());
            }}
            disabled={loading}
            className="px-6 py-3 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 disabled:opacity-50 transition-colors"
          >
            Run All Tests
          </button>
          <button
            onClick={() => setTestResults({})}
            className="px-6 py-3 border border-gray-1 text-text-secondary rounded-lg hover:bg-bg-light transition-colors"
          >
            Clear Results
          </button>
        </div>
      </div>

      {/* Database Schema Info */}
      <div className="bg-bg-primary rounded-xl p-6 ernest-shadow">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Database Schema</h2>
        <div className="bg-bg-light rounded-lg p-4">
          <h3 className="font-medium text-text-primary mb-2">assessments_bf2025 Table:</h3>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• id (UUID, Primary Key)</li>
            <li>• user_name (Text)</li>
            <li>• user_email (Text)</li>
            <li>• total_score (Integer)</li>
            <li>• max_score (Integer, default 54)</li>
            <li>• percentage (Integer)</li>
            <li>• section_scores (JSONB)</li>
            <li>• answers (JSONB)</li>
            <li>• completed_at (Timestamp)</li>
            <li>• created_at (Timestamp)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DatabaseTest;